import { useState, useEffect, useCallback, useMemo } from 'react';

interface UseColumnPreferencesOptions {
    storageKey: string;
    defaultColumns: string[];
    columns: Array<{ id: string }>;
    /**
     * Columns added after users may already have saved preferences. Each is shown once
     * for users whose saved list predates it; hiding it afterwards sticks.
     */
    introducedColumns?: string[];
}

/** Appends introduced columns the user hasn't been shown yet and records them as shown. */
const withIntroducedColumns = (
    storageKey: string,
    savedColumns: string[],
    introducedColumns: string[],
    columns: Array<{ id: string }>
): string[] => {
    const introducedKey = `${storageKey}:introduced`;
    const alreadyIntroduced: string[] = JSON.parse(localStorage.getItem(introducedKey) || '[]');
    const toIntroduce = introducedColumns.filter(
        id => !alreadyIntroduced.includes(id) && columns.some(col => col.id === id)
    );
    if (toIntroduce.length === 0) {
        return savedColumns;
    }

    const nextColumns = [...savedColumns, ...toIntroduce.filter(id => !savedColumns.includes(id))];
    localStorage.setItem(introducedKey, JSON.stringify([...alreadyIntroduced, ...toIntroduce]));
    localStorage.setItem(storageKey, JSON.stringify(nextColumns));
    return nextColumns;
};

export const useColumnPreferences = ({
    storageKey,
    defaultColumns,
    columns,
    introducedColumns = [],
}: UseColumnPreferencesOptions) => {
    const defaultColumnsKey = useMemo(() => defaultColumns.join('|'), [defaultColumns]);

    const [visibleColumns, setVisibleColumns] = useState<string[]>(() => {
        try {
            const saved = localStorage.getItem(storageKey);
            if (saved) {
                const savedColumns = withIntroducedColumns(
                    storageKey,
                    JSON.parse(saved),
                    introducedColumns,
                    columns
                );
                // Filter to only include columns that still exist
                const validColumns = savedColumns.filter((id: string) =>
                    columns.some(col => col.id === id)
                );
                return validColumns.length > 0 ? validColumns : defaultColumns;
            }
            // Defaults already include introduced columns; remember that so hiding one sticks
            if (introducedColumns.length > 0) {
                localStorage.setItem(`${storageKey}:introduced`, JSON.stringify(introducedColumns));
            }
        } catch (error) {
            console.error(`Error loading column preferences from ${storageKey}:`, error);
        }
        return defaultColumns;
    });

    // Initialize visible columns from localStorage or use default columns
    useEffect(() => {
        if (visibleColumns.length === 0 && columns.length > 0) {
            try {
                const saved = localStorage.getItem(storageKey);
                if (saved) {
                    const savedColumns = JSON.parse(saved);
                    // Filter to only include columns that still exist
                    const validColumns = savedColumns.filter((id: string) =>
                        columns.some(col => col.id === id)
                    );
                    if (validColumns.length > 0) {
                        setVisibleColumns(validColumns);
                        return;
                    }
                }
            } catch (error) {
                console.error(`Error loading column preferences from ${storageKey}:`, error);
            }
            // Use default columns if no saved preferences or invalid data
            const defaultCols =
                defaultColumns.length > 0 ? defaultColumns : columns.map(col => col.id);
            setVisibleColumns(defaultCols);
            localStorage.setItem(storageKey, JSON.stringify(defaultCols));
        }
    }, [columns.length, storageKey, defaultColumnsKey, visibleColumns.length]);

    const handleColumnToggle = useCallback(
        (columnId: string) => {
            setVisibleColumns(prev => {
                const newColumns = prev.includes(columnId)
                    ? prev.filter(id => id !== columnId)
                    : [...prev, columnId];
                // Save to localStorage
                try {
                    localStorage.setItem(storageKey, JSON.stringify(newColumns));
                } catch (error) {
                    console.error(`Error saving column preferences to ${storageKey}:`, error);
                }
                return newColumns;
            });
        },
        [storageKey]
    );

    const handleResetColumns = useCallback(() => {
        const defaultCols = defaultColumns.length > 0 ? defaultColumns : columns.map(col => col.id);
        setVisibleColumns(defaultCols);
        // Save to localStorage
        try {
            localStorage.setItem(storageKey, JSON.stringify(defaultCols));
        } catch (error) {
            console.error(`Error saving column preferences to ${storageKey}:`, error);
        }
    }, [defaultColumns, columns, storageKey]);

    /** Set visible columns in one shot (e.g. when applying a view). Persists to localStorage. */
    const setVisibleColumnsFromView = useCallback(
        (columnIds: string[]) => {
            const validIds = columnIds.filter(id => columns.some(col => col.id === id));
            const toApply = validIds.length > 0 ? validIds : defaultColumns;
            setVisibleColumns(toApply);
            try {
                localStorage.setItem(storageKey, JSON.stringify(toApply));
            } catch (error) {
                console.error(`Error saving column preferences to ${storageKey}:`, error);
            }
        },
        [columns, defaultColumns, storageKey]
    );

    return {
        visibleColumns,
        handleColumnToggle,
        handleResetColumns,
        setVisibleColumnsFromView,
    };
};
